import {
  type ViewerMemoryManagerProps,
  type ViewerMemoryConfig,
  type ViewerMemoryConfigMap,
} from './ViewerMemoryManager';
import { useViewerId } from 'hooks/useViewerId';
import { useMemo } from 'react';
import {
  ImagingProviderLoadType,
  type BaseImagingProvider,
} from '../ViewportDre/modules/imaging/BaseImagingProvider';
import { MultiStackImagingProvider } from '../ViewportDre/modules/imaging/MultiStackImagingProvider';
import { MultiObjectImagingProvider } from '../ViewportDre/modules/imaging/MultiObjectImagingProvider';
import type { Stack } from '../ViewportsConfigurations/types';
import { DRE_3D_VIEWPORT_TYPES, SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT } from 'config/constants';
import { logger } from 'modules/logger';
import { FF, useFeatureFlagEnabled, useSplitFlag } from 'modules/feature-flags/suspense';
import {
  collectProvidersInViewports,
  collectProvidersInWindowViewports,
} from './viewportProviders';
import { viewportIdToConfig } from './viewerUtils';
import equals from 'ramda/es/equals';
import { getHybridMammographySwapProviders } from '../ViewportDre/modules/imaging/hybridMammography';

const ONE_GB = 1024 * 1024 * 1024;

// Chrome has a limit around 13.3 GB for Typed Arrays in a single tab
const CHROME_MEMORY_LIMIT = 13 * ONE_GB;

// dedicate 4 GB to a "buffer" for miscellaneous transfer overhead and garbage collection delays
const WORKING_SPACE = 4 * ONE_GB;

// Share of system RAM, after the shared worker's write queue, that all viewer windows may use together.
const SYSTEM_MEMORY_PIXEL_BUDGET_FRACTION = 0.6;

// Low-RAM machines still get enough budget to hang a case.
const MIN_MACHINE_MEMORY_LIMIT = ONE_GB;

// [VX-4318] Providers report nominal pixel bytes; all render overheads are applied here. A volume/MIP
// viewport also holds a GPU-texture copy of the whole stack plus decode scratch (~2x in field data, 1.5x
// to start). Slice viewports upload one slice (VIEWER_SLICE_MEMORY_OVERHEAD_FACTOR). Unhung stacks are 1x.
const RENDERED_MEMORY_OVERHEAD_FACTOR = 1.5;

/** Open viewer windows, counting this one even when the extension's list omits it (see useLayouts). */
export function countViewerWindows({
  openWindowIds,
  windowId,
}: {
  openWindowIds: ReadonlyArray<string>;
  windowId: Nullable<string>;
}): number {
  return new Set(windowId != null ? [...openWindowIds, windowId] : openWindowIds).size;
}

/** Caps the per-window budget at its share of system RAM, floored at 1 GB; never raises it. */
export function getMemoryLimit({
  baseMemoryLimit,
  systemMemoryBytes,
  viewerWindowCount,
}: {
  baseMemoryLimit: number;
  systemMemoryBytes: Nullable<number>;
  viewerWindowCount: number;
}): number {
  if (systemMemoryBytes == null || systemMemoryBytes <= 0) return baseMemoryLimit;
  const availableSystemMemory = systemMemoryBytes - SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT;
  const windowShare =
    (availableSystemMemory * SYSTEM_MEMORY_PIXEL_BUDGET_FRACTION) / Math.max(1, viewerWindowCount);
  return Math.min(baseMemoryLimit, Math.max(MIN_MACHINE_MEMORY_LIMIT, windowShare));
}

const useMemoryLimit = ({
  isMachineBudgetEnabled,
  systemMemoryBytes,
  viewerWindowCount,
}: {
  isMachineBudgetEnabled: boolean;
  systemMemoryBytes: Nullable<number>;
  viewerWindowCount: number;
}) => {
  const memoryFlag = Number(useSplitFlag(FF.VIEWER_MEMORY_OFFSET_GB));
  const memoryOffset = Number.isNaN(memoryFlag) ? 0 : memoryFlag * ONE_GB;
  const baseMemoryLimit =
    CHROME_MEMORY_LIMIT - SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT - WORKING_SPACE + memoryOffset;
  if (!isMachineBudgetEnabled) return baseMemoryLimit;
  return getMemoryLimit({ baseMemoryLimit, systemMemoryBytes, viewerWindowCount });
};

const useSliceMemoryOverheadFactor = () => {
  const factor = Number(useSplitFlag(FF.VIEWER_SLICE_MEMORY_OVERHEAD_FACTOR));
  return Number.isFinite(factor) && factor >= 1 ? factor : RENDERED_MEMORY_OVERHEAD_FACTOR;
};

/**
 * Finds off-screen hybrid counterparts for the direct stacks displayed in one viewer window.
 * Callers may pass the already-collected on-screen set to avoid repeating composite traversal.
 */
export function getHybridMammographySwapProvidersInWindow({
  stackProviders,
  viewportsConfigurations,
  hybridMammographyEnabled,
  viewerId,
  stackProvidersInViewports,
}: Pick<
  ViewerMemoryManagerProps,
  'stackProviders' | 'viewportsConfigurations' | 'hybridMammographyEnabled'
> & {
  viewerId: Nullable<string>;
  stackProvidersInViewports?: ReadonlySet<BaseImagingProvider<Stack>>;
}): Set<BaseImagingProvider<Stack>> {
  const hybridSwapProviders = new Set<BaseImagingProvider<Stack>>();
  if (!hybridMammographyEnabled || viewportsConfigurations == null) {
    return hybridSwapProviders;
  }

  const providersInViewports =
    stackProvidersInViewports ??
    collectProvidersInWindowViewports({
      viewportsConfigurations,
      stackProviders,
      viewerId,
    });
  const directProvidersInViewports = new Set<BaseImagingProvider<Stack>>();
  Object.entries(viewportsConfigurations).forEach(([viewportId, viewportConfiguration]) => {
    if (viewportIdToConfig(viewportId).windowId !== viewerId) return;
    const smid = viewportConfiguration?.stack?.smid;
    const provider = smid ? stackProviders.get(smid) : undefined;
    if (provider) directProvidersInViewports.add(provider);
  });

  directProvidersInViewports.forEach((provider) => {
    getHybridMammographySwapProviders(provider, stackProviders).forEach((match) => {
      if (!providersInViewports.has(match)) hybridSwapProviders.add(match);
    });
  });

  return hybridSwapProviders;
}

// side-effect-free hook to determine what should be loaded at any given time
export function useViewerMemoryConfig({
  stackProviders,
  viewportsConfigurations,
  areViewportsReady,
  hybridMammographyEnabled = false,
  systemMemoryBytes = null,
  viewerWindowCount = 1,
}: ViewerMemoryManagerProps): ViewerMemoryConfig {
  const isMachineBudgetEnabled = useFeatureFlagEnabled(FF.VIEWER_MACHINE_MEMORY_BUDGET);
  const memoryLimit = useMemoryLimit({
    isMachineBudgetEnabled,
    systemMemoryBytes,
    viewerWindowCount,
  });
  const sliceMemoryOverheadFactor = useSliceMemoryOverheadFactor();
  const viewerId = useViewerId();

  return useMemo(() => {
    // 1. Initialize
    const memoryConfigMap: ViewerMemoryConfigMap = new Map();
    // the second check is just for type safety and is one component of `areViewportsReady`
    if (!areViewportsReady || viewportsConfigurations == null) {
      return { memoryLimit, usedMemory: null, memoryConfig: memoryConfigMap };
    }

    // 2. separate out providers not in this window's viewport configuration
    // Composite viewports promote their referenced children so layers don't get demoted to None.
    const stackProvidersInViewports = collectProvidersInWindowViewports({
      viewportsConfigurations,
      stackProviders,
      viewerId,
    });

    const hybridSwapProviders = getHybridMammographySwapProvidersInWindow({
      stackProviders,
      viewportsConfigurations,
      hybridMammographyEnabled,
      viewerId,
      stackProvidersInViewports,
    });

    // Only volume/MIP viewports upload the whole stack to the GPU; slice viewports upload one slice.
    const volumeRenderedProviders = collectProvidersInViewports({
      viewportsConfigurations,
      stackProviders,
      includeViewport: (viewportId, viewportConfiguration) =>
        viewportIdToConfig(viewportId).windowId === viewerId &&
        DRE_3D_VIEWPORT_TYPES.some((viewType) => viewType === viewportConfiguration.viewType),
    });
    const estimateInViewportMemory = (provider: BaseImagingProvider<Stack>) =>
      provider.estimateMemory() *
      (volumeRenderedProviders.has(provider)
        ? RENDERED_MEMORY_OVERHEAD_FACTOR
        : sliceMemoryOverheadFactor);

    // Stacks on screen in another window are already resident there; keep them cache-only here.
    const providersHungInOtherWindows = isMachineBudgetEnabled
      ? collectProvidersInViewports({
          viewportsConfigurations,
          stackProviders,
          includeViewport: (viewportId) => viewportIdToConfig(viewportId).windowId !== viewerId,
        })
      : new Set<BaseImagingProvider<Stack>>();
    const isHungInOtherWindow = (provider: BaseImagingProvider<Stack>) =>
      !hybridSwapProviders.has(provider) && providersHungInOtherWindows.has(provider);

    // Composite providers don't own pixel memory; excluding them avoids double-counting against their children.
    const stackProvidersOutsideViewports = new Set<BaseImagingProvider<Stack>>();
    stackProviders.forEach((p: BaseImagingProvider<Stack>) => {
      if (stackProvidersInViewports.has(p)) return;
      if (p instanceof MultiStackImagingProvider || p instanceof MultiObjectImagingProvider) return;
      stackProvidersOutsideViewports.add(p);
    });

    let remainingMemoryToUse = memoryLimit;

    // 3. Estimate hanged stack providers memory usage
    let idealMemoryForFullLoad = 0;
    const loadedStacksMemoryMap = new Map<BaseImagingProvider<Stack>, number>();
    stackProvidersInViewports.forEach((provider) => {
      const estimate = estimateInViewportMemory(provider);
      idealMemoryForFullLoad += estimate;
      loadedStacksMemoryMap.set(provider, estimate);
    });

    if (idealMemoryForFullLoad > remainingMemoryToUse) {
      stackProvidersInViewports.forEach((provider) => {
        const estimate = estimateInViewportMemory(provider);
        if (estimate <= remainingMemoryToUse) {
          loadedStacksMemoryMap.set(provider, estimate);
          remainingMemoryToUse -= estimate;
          memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.Full);
        } else {
          loadedStacksMemoryMap.set(provider, 0);
          memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.None);
        }
      });

      stackProvidersOutsideViewports.forEach((provider) => {
        loadedStacksMemoryMap.set(provider, 0);
        memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.None);
      });

      logMemoryConfigAfterChange({
        partialHanged: true,
        memoryLimit: `${memoryLimit / ONE_GB} GB`,
        idealMemoryForHangedLoad: `${idealMemoryForFullLoad / ONE_GB} GB`,
        remainingMemoryToUse: `${remainingMemoryToUse / ONE_GB} GB`,
        CHROME_MEMORY_LIMIT: `${CHROME_MEMORY_LIMIT / ONE_GB} GB`,
        WORKING_SPACE: `${WORKING_SPACE / ONE_GB} GB`,
        SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT: `${SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT / ONE_GB} GB`,
        memoryConfigMap: Object.fromEntries(memoryConfigMap),
      });

      return {
        memoryLimit,
        usedMemory: memoryLimit - remainingMemoryToUse,
        memoryConfig: memoryConfigMap,
      };
    }

    // 4. Allocate Hanged Stacks
    loadedStacksMemoryMap.forEach((stackMemory, provider) => {
      remainingMemoryToUse -= stackMemory;
      memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.Full);
    });
    const memoryForHangedStacksFullLoad = idealMemoryForFullLoad;

    let memoryForHybridSwapProvidersFullLoad = 0;
    hybridSwapProviders.forEach((provider) => {
      memoryForHybridSwapProvidersFullLoad += provider.estimateMemory();
    });
    idealMemoryForFullLoad += memoryForHybridSwapProvidersFullLoad;

    hybridSwapProviders.forEach((provider) => {
      const estimate = provider.estimateMemory();
      if (remainingMemoryToUse >= estimate) {
        loadedStacksMemoryMap.set(provider, estimate);
        memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.Full);
        remainingMemoryToUse -= estimate;
        return;
      }

      const estimatePerFrame = provider.estimatePerFrameMemory();
      if (remainingMemoryToUse >= estimatePerFrame && !provider.is3Dable()) {
        loadedStacksMemoryMap.set(provider, estimatePerFrame);
        memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.Initial);
        remainingMemoryToUse -= estimatePerFrame;
        return;
      }

      loadedStacksMemoryMap.set(provider, 0);
      memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.None);
    });

    providersHungInOtherWindows.forEach((provider) => {
      if (!isHungInOtherWindow(provider) || !stackProvidersOutsideViewports.has(provider)) return;
      loadedStacksMemoryMap.set(provider, 0);
      memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.None);
    });

    let memoryForUnhangedStacksFullLoad = 0;
    stackProvidersOutsideViewports.forEach((provider) => {
      if (hybridSwapProviders.has(provider) || isHungInOtherWindow(provider)) return;
      memoryForUnhangedStacksFullLoad += provider.estimateMemory();
    });
    idealMemoryForFullLoad += memoryForUnhangedStacksFullLoad;

    if (remainingMemoryToUse > memoryForUnhangedStacksFullLoad) {
      // 5. Allocate full memory for unhanged stacks
      stackProvidersOutsideViewports.forEach((provider) => {
        if (hybridSwapProviders.has(provider) || isHungInOtherWindow(provider)) return;
        const estimate = provider.estimateMemory();
        loadedStacksMemoryMap.set(provider, estimate);
        memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.Full);
      });
      remainingMemoryToUse -= memoryForUnhangedStacksFullLoad;
    } else {
      // 5. Allocate minimal memory for unhanged non3dable stacks
      stackProvidersOutsideViewports.forEach((provider) => {
        if (hybridSwapProviders.has(provider) || isHungInOtherWindow(provider)) return;
        const estimatePerFrame = provider.estimatePerFrameMemory();

        if (remainingMemoryToUse >= estimatePerFrame && !provider.is3Dable()) {
          loadedStacksMemoryMap.set(provider, estimatePerFrame);
          memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.Initial);
          remainingMemoryToUse -= estimatePerFrame;
        } else {
          loadedStacksMemoryMap.set(provider, 0);
          memoryConfigMap.set(provider.linkSmid, ImagingProviderLoadType.None);
        }
      });
    }

    logMemoryConfigAfterChange({
      partialHanged: false,
      memoryLimit: `${memoryLimit / ONE_GB} GB`,
      idealMemoryForFullLoad: `${idealMemoryForFullLoad / ONE_GB} GB`,
      remainingMemoryToUse: `${remainingMemoryToUse / ONE_GB} GB`,
      memoryForHangedStacksFullLoad: `${memoryForHangedStacksFullLoad / ONE_GB} GB`,
      memoryForHybridSwapProvidersFullLoad: `${memoryForHybridSwapProvidersFullLoad / ONE_GB} GB`,
      memoryForUnhangedStacksFullLoad: `${memoryForUnhangedStacksFullLoad / ONE_GB} GB`,
      CHROME_MEMORY_LIMIT: `${CHROME_MEMORY_LIMIT / ONE_GB} GB`,
      WORKING_SPACE: `${WORKING_SPACE / ONE_GB} GB`,
      SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT: `${SHARED_WORKER_WRITE_QUEUE_MEMORY_LIMIT / ONE_GB} GB`,
      memoryConfigMap: Object.fromEntries(memoryConfigMap),
    });

    return {
      memoryLimit,
      usedMemory: memoryLimit - remainingMemoryToUse,
      memoryConfig: memoryConfigMap,
    };
  }, [
    stackProviders,
    viewportsConfigurations,
    viewerId,
    areViewportsReady,
    memoryLimit,
    hybridMammographyEnabled,
    isMachineBudgetEnabled,
    sliceMemoryOverheadFactor,
  ]);
}

let lastLoggedConfig: Nullable<object> = null;

const logMemoryConfigAfterChange = (config: object) => {
  if (equals(lastLoggedConfig, config)) return;

  lastLoggedConfig = config;
  logger.info('[MEMORY CONFIG]', config);
};

export function clearLastLoggedConfig() {
  lastLoggedConfig = null;
}
